import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Promotion } from '@model/promotion';
import { HomeType } from '@model/home-type';
import { PromotionService } from '@service/promotion/promotion.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

interface PromotionConfig {
  id?: number;
  uuid?: string;
  promotion?: string;
  options: OptionConfig[];
}

interface OptionConfig {
  id?: number;
  uuid?: string;
  type: string;
  taux: number;
  mois: number;
  libelle?: string;
}

@Component({
  selector: 'app-promotion-config',
  templateUrl: './promotion-config.component.html',
  styleUrls: ['./promotion-config.component.scss']
})
export class PromotionConfigComponent implements OnInit {
  form: FormGroup;
  promotion: Promotion;
  promotionUuid: string;
  homeTypes: HomeType[] = [];
  submit: boolean = false;
  loading: boolean = false;
  
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public modal: NgbActiveModal,
    private promotionService: PromotionService,
    private toastr: ToastrService
  ) {
    this.promotion = this.promotionService.getPromotion();
    this.createForm();
  }

  ngOnInit(): void {
    this.loadPromotionData();
  }

  createForm(): void {
    this.form = this.fb.group({
      uuid: [null],
      promotion: [null, Validators.required],
      options: this.fb.array([]),
    });
  }

  get optionsFormArray(): FormArray {
    return this.form.get('options') as FormArray;
  }

  // Add these getter methods to handle complex calculations in template
  get averageRate(): number {
    const options = this.optionsFormArray.value || [];
    if (options.length === 0 || this.homeTypes.length === 0) return 0;
    const sum = options.reduce((total: number, opt: any) => total + (opt.taux || 0), 0);
    return Number((sum / this.homeTypes.length).toFixed(2));
  }

  get averageDuration(): number {
    const options = this.optionsFormArray.value || [];
    if (options.length === 0 || this.homeTypes.length === 0) return 0;
    const sum = options.reduce((total: number, opt: any) => total + (opt.mois || 0), 0);
    return Math.round(sum / this.homeTypes.length);
  }

  get configuredTypesCount(): number {
    const options = this.optionsFormArray.value || [];
    return options.filter((opt: any) => opt.taux > 0 && opt.mois > 0).length;
  }

  loadPromotionData(): void {
    if (this.promotion) {
      this.promotion = this.promotion;
      this.homeTypes = this.promotion.typeHome || [];
      this.form.patchValue({
        promotion: this.promotion.uuid
      });
      
      this.loadExistingConfig();
      this.initializeOptionsForm();
    }
  }

  loadExistingConfig(): void {
    this.loading = false;
  }

  populateExistingOptions(existingOptions: OptionConfig[]): void {
    this.optionsFormArray.clear();
    
    this.homeTypes.forEach(homeType => {
      const existingOption = existingOptions.find(opt => opt.type === homeType.uuid);
      
      const optionGroup = this.fb.group({
        uuid: [existingOption?.uuid || null],
        type: [homeType.uuid, Validators.required],
        libelle: [homeType.libelle],
        taux: [existingOption?.taux || 0, [Validators.required, Validators.min(0), Validators.max(100)]],
        mois: [existingOption?.mois || 12, [Validators.required, Validators.min(1), Validators.max(1000)]]
      });
      
      this.optionsFormArray.push(optionGroup);
    });
  }

  initializeOptionsForm(): void {
    if (this.optionsFormArray.length === 0) {
      this.homeTypes.forEach(homeType => {
        const optionGroup = this.fb.group({
          uuid: [null],
          type: [homeType.uuid, Validators.required],
          libelle: [homeType.libelle],
          taux: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
          mois: [12, [Validators.required, Validators.min(1), Validators.max(1000)]]
        });
        
        this.optionsFormArray.push(optionGroup);
      });
    }
  }

  onSubmit(): void {
    this.submit = true;
    
    if (this.form.valid) {
      Swal.fire({
        title: 'Confirmation',
        text: 'Êtes-vous sûr de vouloir enregistrer cette configuration ?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirmer',
        cancelButtonText: 'Annuler'
      }).then((result) => {
        if (result.isConfirmed) {
          this.saveConfiguration();
        }
      });
    } else {
      this.toastr.error('Veuillez corriger les erreurs du formulaire', 'Erreur');
    }
  }

  saveConfiguration(): void {
    this.loading = true;
    const formData = this.form.value;
      
    this.promotionService.saveConfig(formData).subscribe(res => {
      if (res?.status === 'success') {
        this.modal.close('ferme');
        // if (formData.uuid) {
        //   this.emitter.emit({ action: 'PROMOTION_UPDATED', payload: res?.data });
        // } else {
        //   this.emitter.emit({ action: 'PROMOTION_ADD', payload: res?.data });
        // }
      }
    }, error => { 
      console.error('Erreur lors de la soumission:', error);
    });
    
    // Implementation for saving configuration
    this.loading = false;
  }

  resetForm(): void {
    Swal.fire({
      title: 'Confirmation',
      text: 'Êtes-vous sûr de vouloir réinitialiser le formulaire ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.optionsFormArray.clear();
        this.initializeOptionsForm();
        this.submit = false;
        this.toastr.info('Formulaire réinitialisé', 'Info');
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/promotions']);
  }

  getOptionControl(index: number, controlName: string): any {
    return this.optionsFormArray.at(index).get(controlName);
  }

  hasError(index: number, controlName: string): boolean {
    const control = this.getOptionControl(index, controlName);
    return control && control.invalid && (control.dirty || control.touched || this.submit);
  }

  getErrorMessage(index: number, controlName: string): string {
    const control = this.getOptionControl(index, controlName);
    if (control?.errors) {
      if (control.errors['required']) return 'Ce champ est requis';
      if (control.errors['min']) return `La valeur minimum est ${control.errors['min'].min}`;
      if (control.errors['max']) return `La valeur maximum est ${control.errors['max'].max}`;
    }
    return '';
  }

}